import React, { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api, saveSessionToken, TOKEN_KEY } from "@/lib/api";
import { clientLog } from "@/lib/clientLog";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import NewRequest from "@/pages/NewRequest";
import ApprovalFeed from "@/pages/ApprovalFeed";
import PaymentQueue from "@/pages/PaymentQueue";
import RequestDetail from "@/pages/RequestDetail";
import AdminPanel from "@/pages/AdminPanel";
import Reports from "@/pages/Reports";
import MyRequests from "@/pages/MyRequests";
import InventoryHome from "@/pages/inventory/InventoryHome";
import ReconciliationReport from "@/pages/inventory/ReconciliationReport";
import WriteOffReview from "@/pages/inventory/WriteOffReview";
import ConfidentialVault from "@/pages/ConfidentialVault";
import LicensesPage from "@/pages/LicensesPage";
import CashFlowPage from "@/pages/CashFlowPage";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function AuthCallback() {
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const session_id = match ? match[1] : null;

    clientLog("callback_mount", "AuthCallback mounted", {
      has_hash: Boolean(hash),
      hash_len: hash.length,
      has_session_id: Boolean(session_id),
      ua_mobile: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent || ""),
    });

    const run = async () => {
      if (!session_id) {
        clientLog("callback_no_session_id", "Hash missing session_id; redirecting to /login", {});
        window.location.replace("/login");
        return;
      }

      // Pre-flight: make sure the backend is reachable. If the very first call
      // is /auth/session and it fails with axios "Network Error", we cannot
      // distinguish "backend down" from "session_id rejected" — the user just
      // sees "Network Error" with no actionable info. A lightweight probe to
      // /api/auth/health surfaces real connectivity issues immediately.
      try {
        await api.get("/auth/health", { timeout: 8000 });
        clientLog("health_ok", "Backend reachable", {});
      } catch (he) {
        const netMsg = he?.message || "Network Error";
        clientLog("health_fail", netMsg, {
          code: he?.code,
          status: he?.response?.status,
        });
        try {
          sessionStorage.setItem(
            "vowos_login_error",
            `Could not reach VOW OS backend (${netMsg}). Check your internet connection and try again.`
          );
        } catch (err) { /* noop */ }
        window.location.replace("/login");
        return;
      }

      try {
        clientLog("exchange_start", "POST /auth/session", { sid_prefix: session_id.slice(0, 8) });
        const res = await api.post("/auth/session", { session_id });

        // Persist Bearer token BEFORE any navigation. Mobile Safari blocks 3rd-party
        // cookies; localStorage is our reliable channel.
        const token = res.data?.session_token;
        if (token) {
          saveSessionToken(token);
        }

        // Verify persistence actually worked (some WebViews/private-mode silently drop localStorage).
        let persisted = false;
        try {
          persisted = localStorage.getItem(TOKEN_KEY) === token;
        } catch (e) { /* noop */ }

        clientLog("exchange_success", "Session established", {
          has_token: Boolean(token),
          persisted,
          user_email: res.data?.user?.email,
          is_mobile: res.data?.is_mobile,
        });

        // Use a full-page replace instead of SPA navigate to avoid state-sync races
        // on mobile where ProtectedRoute can read user=null before setUser commits.
        // AuthContext will re-boot cleanly, read the Bearer token, and call /auth/me.
        window.location.replace("/dashboard");
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        const axiosMsg = e?.message;
        // Distinguish: backend-rejected (4xx/5xx with detail) vs transport (Network Error/timeout)
        let userMsg;
        if (status && detail) {
          userMsg = detail;
        } else if (e?.code === "ECONNABORTED" || /timeout/i.test(axiosMsg || "")) {
          userMsg = "Login timed out. Please check your connection and try again.";
        } else if (axiosMsg === "Network Error") {
          userMsg = "Could not reach VOW OS backend. Please verify your internet connection and retry.";
        } else {
          userMsg = detail || axiosMsg || "Login failed";
        }
        clientLog("exchange_failure", userMsg, {
          status,
          code: e?.code,
          axios_message: axiosMsg,
        });
        try {
          sessionStorage.setItem("vowos_login_error", userMsg);
        } catch (err) { /* noop */ }
        window.location.replace("/login");
      }
    };
    run();
  }, [location]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F4F2]">
      <div className="label-micro">AUTHENTICATING…</div>
    </div>
  );
}

function ProtectedRoute({ children, roles, perm, vertical }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="label-micro">LOADING…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // v2.11 — role OR permission gate. If `perm` is given, allowing that
  // permission is sufficient regardless of role (b2 read-only pattern).
  // If both are missing, the route is authenticated-only.
  const roleAllows = !roles || roles.includes(user.role);
  const permAllows = perm ? !!user?.permissions_effective?.[perm] : false;
  if ((roles || perm) && !roleAllows && !permAllows) {
    return <Navigate to="/dashboard" replace />;
  }
  if (vertical) {
    const verticals = user.verticals || ["finance"];
    if (!verticals.includes(vertical)) {
      const fallback = verticals.includes("finance") ? "/dashboard" : "/inventory";
      return <Navigate to={fallback} replace />;
    }
  }
  return children;
}

/** Decides where "/" should land based on the user's verticals.
 *  Finance-only → /dashboard. Inventory-only → /inventory.
 *  Both (or admin) → /dashboard (finance is the primary workspace). */
function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="label-micro">LOADING…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  const verticals = user.verticals || ["finance"];
  if (!verticals.includes("finance") && verticals.includes("inventory")) {
    return <Navigate to="/inventory" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

function AppRouter() {
  const location = useLocation();

  // Handle session_id in URL hash first (synchronous check)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute vertical="finance">
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/new-request"
        element={
          <ProtectedRoute vertical="finance">
            <NewRequest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-requests"
        element={
          <ProtectedRoute vertical="finance">
            <MyRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/approvals"
        element={
          <ProtectedRoute roles={["approver", "admin"]} perm="view_payment_queue" vertical="finance">
            <ApprovalFeed />
          </ProtectedRoute>
        }
      />
      <Route
        path="/queue"
        element={
          <ProtectedRoute roles={["accounts", "admin"]} perm="view_payment_queue" vertical="finance">
            <PaymentQueue />
          </ProtectedRoute>
        }
      />
      <Route
        path="/requests/:id"
        element={
          <ProtectedRoute vertical="finance">
            <RequestDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute roles={["admin"]}>
            <AdminPanel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute roles={["admin", "approver", "accounts"]} vertical="finance">
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/confidential"
        element={
          <ProtectedRoute vertical="finance">
            <ConfidentialVault />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cashflow"
        element={
          <ProtectedRoute vertical="finance">
            <CashFlowPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/licenses"
        element={
          <ProtectedRoute vertical="licenses">
            <LicensesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <ProtectedRoute vertical="inventory">
            <InventoryHome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/reports"
        element={
          <ProtectedRoute vertical="inventory">
            <ReconciliationReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/write-offs"
        element={
          <ProtectedRoute vertical="inventory">
            <WriteOffReview />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-center" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
